import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ErrorBoundary from "./components/UI/ErrorBoundary";
import usePageTracking from "./hooks/usePageTracking";
import { useUTMTracking } from "./hooks/useUTMTracking";
import useFeatureFlags from "./hooks/useFeatureFlags";
import useABTest from "./hooks/useABTest";
import PageTransition from "./components/UI/PageTransition";
import { PageSkeleton } from "./components/UI/SkeletonLoader";
import BackToTop from "./components/UI/BackToTop";
import ScrollProgress from "./components/UI/ScrollProgress";
import CookieConsent from "./components/UI/CookieConsent";
import WhatsAppButton from "./components/UI/WhatsAppButton";
import CommandPalette from "./components/UI/CommandPalette";
import OfferBanner from "./components/UI/OfferBanner";
import AnnouncementBar from "./components/UI/AnnouncementBar";
import ErrorTracker from "./components/Integrations/ErrorTracker";
import GoogleTagManager from "./components/Integrations/GoogleTagManager";

// Lazy-load non-critical widgets for better INP and LCP
const SocialProofToast = lazy(() => import("./components/UI/SocialProofToast"));
const MobileBottomNav = lazy(() => import("./components/UI/MobileBottomNav"));
const AccessibilityWidget = lazy(() => import("./components/UI/AccessibilityWidget"));
const SeasonalTheme = lazy(() => import("./components/UI/SeasonalTheme"));
const ExitIntentPopup = lazy(() => import("./components/UI/ExitIntentPopup"));
const LiveChat = lazy(() => import("./components/Integrations/LiveChat"));
const HeatmapTracker = lazy(() => import("./components/Integrations/HeatmapTracker"));
const ChatbotWidget = lazy(() => import("./components/Integrations/ChatbotWidget"));

// Main pages
const Home = lazy(() => import("./pages/main-site/Home"));
const About = lazy(() => import("./pages/main-site/About"));
const Services = lazy(() => import("./pages/main-site/Services"));
const CaseStudies = lazy(() => import("./pages/main-site/CaseStudies"));
const Team = lazy(() => import("./pages/main-site/Team"));
const FAQ = lazy(() => import("./pages/main-site/FAQ"));
const Blog = lazy(() => import("./pages/main-site/Blog"));
const BlogPost = lazy(() => import("./pages/main-site/BlogPost"));
const Contact = lazy(() => import("./pages/main-site/Contact"));
const Support = lazy(() => import("./pages/main-site/Support"));

// Tools & Calculators
const CostCalculator = lazy(() => import("./pages/main-site/CostCalculator"));
const ROICalculator = lazy(() => import("./pages/main-site/ROICalculator"));
const Quiz = lazy(() => import("./pages/main-site/Quiz"));
const ProposalRequest = lazy(() => import("./pages/main-site/ProposalRequest"));

// Info pages
const TechStack = lazy(() => import("./pages/main-site/TechStack"));
const Changelog = lazy(() => import("./pages/main-site/Changelog"));
const StatusPage = lazy(() => import("./pages/main-site/StatusPage"));
const HTMLSitemap = lazy(() => import("./pages/main-site/HTMLSitemap"));
const Comparison = lazy(() => import("./pages/main-site/Comparison"));
const Industries = lazy(() => import("./pages/main-site/Industries"));
const IndustryDetail = lazy(() => import("./pages/main-site/IndustryDetail"));
const Glossary = lazy(() => import("./pages/main-site/Glossary"));
const AppStatsDashboard = lazy(() => import("./pages/main-site/AppStatsDashboard"));
const WebinarRegistration = lazy(() => import("./pages/main-site/WebinarRegistration"));
const AppLaunchCountdown = lazy(() => import("./pages/main-site/AppLaunchCountdown"));

// Legal
const TermOfUse = lazy(() => import("./pages/main-site/TermOfUse"));
const Policy = lazy(() => import("./pages/main-site/Policy"));
const NotFound = lazy(() => import("./pages/main-site/NotFound"));

// App landing pages
const GPSMapCamera = lazy(() => import("./pages/landing-pages/gps-map-camera/GPSMapCamera"));
const BirthdayReminder = lazy(() => import("./pages/landing-pages/birthday-reminder/BirthdayReminder"));
const AiCloset = lazy(() => import("./pages/landing-pages/ai-closet/AiCloset"));
const LedScroller = lazy(() => import("./pages/landing-pages/led-scroller/LedScroller"));

function PageTracker({ children }) {
  usePageTracking();
  useUTMTracking();
  const { isEnabled } = useFeatureFlags();
  const { variant: heroCTAVariant } = useABTest("hero-cta", ["book-call", "get-started"]);

  return (
    <>
      {children}
      {/* Feature flags and A/B test values available via context if needed */}
      <input type="hidden" data-ab-hero={heroCTAVariant} data-flags-offer={isEnabled("showOfferBanner")} />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <PageTracker>
          {/* Top bars */}
          <OfferBanner />
          <AnnouncementBar />
          <ScrollProgress />
          <CommandPalette />

          <Suspense fallback={<PageSkeleton />}>
            <PageTransition>
            <Routes>
              {/* Main */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/team" element={<Team />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/support" element={<Support />} />

              {/* Tools */}
              <Route path="/cost-calculator" element={<CostCalculator />} />
              <Route path="/roi-calculator" element={<ROICalculator />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/request-proposal" element={<ProposalRequest />} />

              {/* Info */}
              <Route path="/tech-stack" element={<TechStack />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/sitemap" element={<HTMLSitemap />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/app-stats" element={<AppStatsDashboard />} />
              <Route path="/webinar" element={<WebinarRegistration />} />
              <Route path="/coming-soon" element={<AppLaunchCountdown />} />

              {/* Legal */}
              <Route path="/terms-of-use" element={<TermOfUse />} />
              <Route path="/privacy-policy" element={<Policy />} />

              {/* App Landing Pages */}
              <Route path="/app/gpsmapcamera" element={<GPSMapCamera />} />
              <Route path="/app/birthdayreminder" element={<BirthdayReminder />} />
              <Route path="/app/aicloset" element={<AiCloset />} />
              <Route path="/app/ledscroller" element={<LedScroller />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </PageTransition>
          </Suspense>

          {/* Global floating UI */}
          <BackToTop />
          <WhatsAppButton />
          <CookieConsent />
          <Suspense fallback={null}>
            <ChatbotWidget />
            <SocialProofToast />
            <ExitIntentPopup />
            <MobileBottomNav />
            <AccessibilityWidget />
            <SeasonalTheme />
          </Suspense>

          {/* Analytics & tracking (render nothing) */}
          <Suspense fallback={null}>
            <LiveChat />
            <HeatmapTracker />
          </Suspense>
          <ErrorTracker />
          <GoogleTagManager />
        </PageTracker>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
